import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/ui/button';
import { Input, Field } from '@/ui/input';
import { MicroLabel } from '@/ui/micro-label';
import { Wordmark } from '@/domain/wordmark';
import { OAuthButtons } from './../shared/oauth-buttons';

/** Vendor portal sign-in: centred card on the page surface. */
export function VendorLogin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // A vendor arriving from the join screen has just proved which account is theirs, so their email
  // is carried across rather than made to be retyped. Everyone else starts empty: the fallback used
  // to be a real demo account with its password already filled in, which is fine on a laptop and a
  // published credential once the bundle is served to the internet.
  const invited = (useLocation().state as { email?: string } | null)?.email;
  const [email, setEmail] = useState(invited ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/login', {
        method: 'POST',
        realm: 'vendor',
        body: JSON.stringify({ email, password })
      });
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      navigate('/v');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Sign in failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4 py-12">
      <div className="w-full max-w-[352px]">
        <div className="grid justify-items-center gap-3">
          <Wordmark height={16} />
          <span className="rounded-[3px] border border-border px-2 py-1 font-mono text-[10px] tracking-[0.12em] text-muted uppercase">
            Vendor portal
          </span>
        </div>

        <form onSubmit={submit} className="mt-8 grid gap-4 rounded border border-border bg-card px-8 py-7">
          <div className="grid gap-1.5">
            <h1 className="text-[22px] font-medium text-foreground">Sign in</h1>
            <p className="text-[13px] text-muted">Submit, track, and remediate your data batches.</p>
          </div>

          <OAuthButtons providers={['google', 'microsoft']} />

          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-border" />
            <MicroLabel size="xs">or</MicroLabel>
            <span className="h-px flex-1 bg-border" />
          </div>

          <Field label="Work email" htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="username"
              placeholder="ops@yourcompany.com"
              value={email}
              onChange={event => setEmail(event.target.value)}
              required
            />
          </Field>

          <Field label="Password" htmlFor="password">
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              required
            />
          </Field>

          {error ? <p className="text-[13px] text-danger">{error}</p> : null}

          <Button type="submit" disabled={busy} className="mt-1 w-full">
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>

          <p className="text-center text-[11px] leading-relaxed text-muted">
            Access is by invitation only. Received an invite from Miraxis? Open your invite link to register your
            organization.
          </p>
        </form>

        <MicroLabel size="xs" caps={false} className="mt-6 block text-center">
          miraxis.ai · vendor support: partners@miraxis.ai
        </MicroLabel>
      </div>
    </div>
  );
}
